import type {
  AttachmentImageMetaInfo,
  AttachmentMetaInfo,
  ContentHref,
} from '@generalTypes/apiTypes';
import {
  selectContentItem,
  selectBaselineContentItem,
} from '@newStore/documentApi/documentApiSelectors';
import { htmlDiffFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { createTypedSelector, getAttachmentUrl } from '@newStore/genericHelpers';
import {
  type AsideChangeMessageSelector,
  type EditAsideImage,
  isEditAsideImage,
} from '@nodeTypeConfig/configTypes';
import { selectCurrentEditingNode } from '@store/selectors/asideSelectors';
import { get, pick, isEqual } from 'lodash';

export const selectAttachmentsOfType = createTypedSelector(
  [
    (state, config: EditAsideImage) => get(selectCurrentEditingNode(state), config.property),
    (state, config: EditAsideImage) => config.options.type,
  ],
  (attachments, type) => {
    return attachments.filter(
      (a: AttachmentMetaInfo) => a.type === type,
    ) as AttachmentImageMetaInfo[];
  },
);

const findAttachment = (content, config, type) =>
  content?.[config.property]?.find(
    (a: AttachmentMetaInfo) => a.type === type && !('resized' in a),
  ) as AttachmentImageMetaInfo | undefined;

const generateHtml = (attachment, addAlt) => {
  if (!attachment) {
    return '';
  }
  return `
    <img src="${getAttachmentUrl(attachment)}" />
    ${addAlt ? `<br />Alt tag: ${attachment.alt || ''}` : ''}
  `;
};

export const selectChangeMessageForImage: AsideChangeMessageSelector<EditAsideImage> =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectContentItem(state, href),
      (state, href: ContentHref) => selectBaselineContentItem(state, href),
      (state, href: ContentHref, config: EditAsideImage) => config,
    ],
    (content, originalContent, config): string | null => {
      if (!config.property) {
        console.error('No property found for config', config);
        throw Error(`Can not convert ${config.property} to a string`);
      }

      if (!isEditAsideImage(config)) {
        console.error('config is not for an image', config);
        throw Error('config is not for an image');
      }

      const { type } = config.options;
      const newValue = findAttachment(content, config, type);
      const oldValue = findAttachment(originalContent, config, type);

      const attPropertiesWeCareAbout = [
        'key',
        'name',
        'type',
        'contentType',
        'width',
        'height',
        'alt',
      ];

      const newFilteredValue = pick(newValue, attPropertiesWeCareAbout);
      const oldFilteredValue = pick(oldValue, attPropertiesWeCareAbout);

      if (isEqual(newFilteredValue, oldFilteredValue)) {
        return null;
      }

      const addAlt = config.options.fields?.includes('alt') && newValue?.alt !== oldValue?.alt;

      const newHtml = generateHtml(newValue, addAlt);
      const oldHtml = generateHtml(oldValue, addAlt);

      const diffHtml = htmlDiffFunction(newHtml, oldHtml);

      return diffHtml;
    },
  );
